import { FirePerimeterMethod, GeometryType, SidePanelLayerType, ToolName } from "@nova/constants"
import { DeckGLGeometry, Geometry } from "plugins/shared/layers/types"
import { withRoot } from "@nova/with-root"
import {
    createAsyncThunk,
    createListenerMiddleware,
    createSelector,
    createSlice,
    isAnyOf,
} from "@reduxjs/toolkit"
import { RootState } from "integrations/redux/rootReducer"
import {
    addLayerToSelection,
    clearSelection,
    removeLayerFromSelection,
    selectLastSelectedLayerId,
    selectLayerRange,
    selectSelectedLayers,
    selectSingleLayer,
} from "panels/content/side-panel/slice.new"
import { Coordinate, Perimeter } from "@nova/protobuf"
import { ensurePolygonIsClosed } from "@nova/calculate-geospatial"
import { mergePolygonsIfTheyIntersect } from "@nova/calculate-union"
import { setSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { selectVisibleFirePerimeters } from "features/entity-caching/fire-perimeters/selectors"

export const MOCKED_PERIMETER_ID = "MOCKED_NEW_PERIMETER_ID"

type initialLineStateType = {
    updateMethod: FirePerimeterMethod
    mockedName: string
    previewPolygons: Coordinate[][]
    mergedPreviewPolygons: Geometry[]

    editing:
        | {
              id: string
              // -1 means new mocked polygon
              polygonIndex: number
              mockedPerimeter: DeckGLGeometry
          }
        | undefined
    canConfirm: boolean
}
const initialLineState: initialLineStateType = {
    updateMethod: FirePerimeterMethod.None,
    existingPolygonIndex: -1,
    mockedName: "Fire Perimeter",

    previewPolygons: [],
    mergedPreviewPolygons: [],

    editing: undefined,
    canConfirm: false,
} as initialLineStateType

const name = "fire-perimeter-v2" as const

/**
 * Merges overlapping preview polygons into unified geometries.
 * Automatically cancels previous merge operations when a new one starts.
 */
let previewPolygonsAbortController: AbortController | undefined
export const mergePreviewPolygons = createAsyncThunk<Geometry[], Coordinate[][]>(
    "fire-perimeters/mergePreviewPolygons",
    async (polygons, { signal }) => {
        previewPolygonsAbortController?.abort()
        previewPolygonsAbortController = new AbortController()

        const abortSignal = previewPolygonsAbortController.signal
        const mergedPolygons = await mergePolygonsIfTheyIntersect(polygons, abortSignal)

        if (abortSignal.aborted || signal.aborted) {
            throw new Error("Aborted")
        }

        return mergedPolygons.map(
            (coordinates) =>
                ({
                    coordinates: ensurePolygonIsClosed(coordinates),
                    geometryType: GeometryType.Polygon,
                }) satisfies Geometry
        )
    }
)

const {
    reducer: firePerimeterReducer,
    actions: {
        changeUpdateMethod,
        addPolygonToFirePerimeterPreview,
        addMultiplePolygonsToFirePerimeterPreview,
        removePolygonFromFirePerimeterPreviewAtIndex,
        resetFirePerimeterPreview,
        setFirePerimeterPreviewPolygon,
        moveFirePerimeterToEditable,
        setMockedFirePerimeterName,
        createNewMockedPerimeter,
        createNewMockedPolygon,
        clearEditingData,
        clearEditingDataNoSideEffects,
        setMergedPreviewPolygons,
        updatePerimeterCanConfirm,
    },
} = createSlice({
    name: "fire-perimeters",
    initialState: {
        ...initialLineState,
    },
    reducers: {
        changeUpdateMethod: (state, action: { payload: { method: FirePerimeterMethod } }) => {
            state.updateMethod = action.payload.method
        },
        /** DO NOT iterate calls of this. Instead, use addMultiplePolygonsToFirePerimeterPreview. */
        addPolygonToFirePerimeterPreview: (state, action: { payload: Coordinate[] }) => {
            state.previewPolygons = [...state.previewPolygons, action.payload]
        },
        addMultiplePolygonsToFirePerimeterPreview: (state, action: { payload: Coordinate[][] }) => {
            state.previewPolygons = [...state.previewPolygons, ...action.payload]
        },
        setFirePerimeterPreviewPolygon: (state, action: { payload: Coordinate[] }) => {
            state.previewPolygons = action.payload.length >= 3 ? [action.payload] : []
        },
        removePolygonFromFirePerimeterPreviewAtIndex: (state, action: { payload: number }) => {
            const polygons = [...state.previewPolygons]
            if (!polygons[action.payload]) return

            polygons.splice(action.payload, 1)
            state.previewPolygons = polygons
        },
        resetFirePerimeterPreview: (state) => {
            state.previewPolygons = []
        },
        setMockedFirePerimeterName: (state, { payload }: { payload: string }) => {
            state.mockedName = payload
        },
        moveFirePerimeterToEditable: (
            state,
            {
                payload: { perimeter, currentIndex },
            }: { payload: { perimeter: Perimeter; currentIndex?: number } }
        ) => {
            if (!currentIndex) currentIndex = 0
            state.editing = {} as initialLineStateType["editing"]
            state.editing!.polygonIndex = currentIndex
            state.editing!.id = perimeter.id
            if (perimeter.polygons.length) {
                state.editing!.mockedPerimeter = {
                    geometryType: GeometryType.Polygon,
                    coordinates: perimeter.polygons[currentIndex].coordinates,
                    id: perimeter.id,
                    color: perimeter.outline,
                    width: perimeter.width,
                    idx: 0,
                }
            }
        },
        createNewMockedPerimeter: (state) => {
            state.editing = {} as initialLineStateType["editing"]
            state.editing!.mockedPerimeter = {
                geometryType: GeometryType.Line,
                coordinates: [],
                id: MOCKED_PERIMETER_ID,
                idx: 0,
            }

            state.editing!.polygonIndex = 0
            state.editing!.id = MOCKED_PERIMETER_ID
        },
        createNewMockedPolygon: (state) => {
            if (!state.editing)
                return console.warn(
                    "Tried to create a new mocked polygon without editing a perimeter"
                )
            state.editing.mockedPerimeter = {
                geometryType: GeometryType.Line,
                coordinates: [],
                id: state.editing.id,
                color: state.editing.mockedPerimeter?.color,
                width: state.editing.mockedPerimeter?.width,
                idx: 0,
            }
            state.editing.polygonIndex = -1
        },
        clearEditingData: (state) => {
            state.editing = undefined
            state.previewPolygons = []
        },
        // above action will cleanup active tool name, current geometry, and more
        clearEditingDataNoSideEffects: (state) => {
            state.editing = undefined
            state.previewPolygons = []
        },
        setMergedPreviewPolygons: (state, action: { payload: Geometry[] }) => {
            state.mergedPreviewPolygons = action.payload
        },
        updatePerimeterCanConfirm: (state, action: { payload: boolean }) => {
            state.canConfirm = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setSnapshotOpen, (_, action) => {
                if (!action.payload) return
                return {
                    ...initialLineState,
                }
            })
            .addCase(mergePreviewPolygons.fulfilled, (state, action) => {
                state.mergedPreviewPolygons = action.payload
            })
            .addMatcher(
                isAnyOf(
                    selectSingleLayer,
                    addLayerToSelection,
                    removeLayerFromSelection,
                    selectLayerRange,
                    clearSelection
                ),
                () => {
                    return {
                        ...initialLineState,
                    }
                }
            )
    },
})

const {
    selectUpdateMethod,
    selectEditingFirePerimeterId,
    selectMockedPerimeterName,
    selectEditingPolygonIndex,
    selectEditingData,
} = withRoot((r: RootState) => r[name], {
    selectUpdateMethod: () => (s) => s.updateMethod,
    selectEditingPolygonIndex: () => (s) => s.editing?.polygonIndex,
    selectEditingFirePerimeterId: () => (s) => s.editing?.id,
    selectMockedPerimeterName: () => (s) => s.mockedName,
    selectEditingData: () => (s) => s.editing,
})

const selectSelectedFirePerimeterId = createSelector(
    [selectLastSelectedLayerId, selectSelectedLayers],
    (lastSelectedLayerId, selectedLayers) =>
        selectedLayers.find((layer) => layer.id === lastSelectedLayerId)?.type ===
        SidePanelLayerType.FirePerimeter
            ? lastSelectedLayerId
            : undefined
)

export const FirePerimeterReducer = firePerimeterReducer

export {
    changeUpdateMethod,
    selectUpdateMethod,
    selectSelectedFirePerimeterId,
    selectEditingFirePerimeterId,
    addPolygonToFirePerimeterPreview,
    addMultiplePolygonsToFirePerimeterPreview,
    removePolygonFromFirePerimeterPreviewAtIndex,
    resetFirePerimeterPreview,
    setFirePerimeterPreviewPolygon,
    selectEditingPolygonIndex,
    moveFirePerimeterToEditable,
    setMockedFirePerimeterName,
    createNewMockedPerimeter,
    selectMockedPerimeterName,
    selectEditingData,
    createNewMockedPolygon,
    clearEditingData,
    clearEditingDataNoSideEffects,
    updatePerimeterCanConfirm,
}

export const reducer = { [name]: FirePerimeterReducer }

const root = (state: RootState) => state[name]

export const selectFirePerimetersToDraw = createSelector(
    [selectVisibleFirePerimeters],
    (perimeters) => {
        return perimeters.flatMap((perimeter) =>
            perimeter.polygons.reduce((geometries, polygon, i) => {
                geometries.push({
                    geometryType: GeometryType.Polygon,
                    coordinates: polygon.coordinates,
                    id: perimeter.id,
                    color: perimeter.outline,
                    width: perimeter.width,
                    idx: i,
                } as DeckGLGeometry)

                return geometries
            }, [] as DeckGLGeometry[])
        )
    }
)

export const selectPreviewPolygons = (state: RootState) => root(state).previewPolygons
export const selectCanConfirmPerimeter = (state: RootState) => root(state).canConfirm
/**
 * Listener middleware that automatically merges preview polygons whenever they change.
 *
 * This middleware watches for changes to the `previewPolygons` state and automatically
 * triggers the `mergePreviewPolygons` thunk to compute the merged result. When preview
 * polygons change, any merge operation is cancelled and a new one is started.
 * The merged result is then stored in `mergedPreviewPolygons` state.
 *
 * This provides automatic side-effect handling within the slice, eliminating the need
 * for manual dispatch calls after every action that modifies preview polygons.
 */
export const firePerimeterListenerMiddleware = createListenerMiddleware<RootState>()
firePerimeterListenerMiddleware.startListening({
    predicate: (_, currentState, previousState) => {
        const prev = selectPreviewPolygons(previousState)
        const curr = selectPreviewPolygons(currentState)
        return prev !== curr
    },
    effect: async (_, listenerApi) => {
        listenerApi.cancelActiveListeners()
        const state = listenerApi.getState()
        const previewPolygons = selectPreviewPolygons(state)

        if (previewPolygons.length > 0) {
            await listenerApi.dispatch(mergePreviewPolygons(previewPolygons))
        } else {
            listenerApi.dispatch(setMergedPreviewPolygons([]))
        }
    },
})

export const selectPreviewFirePerimeterPolygons = createSelector(
    root,
    (state) => state.mergedPreviewPolygons
)

export const selectIsEditingOrDrawingSomeFirePerimeter = createSelector(
    // Duplicating selectActiveToolName to avoid circular import
    [(s: RootState) => s["toolbox-registry-UI"].active],
    (activeTool) => activeTool === ToolName.FirePerimeter
)

export const selectNextFirePerimeterName = createSelector([root], (state) => state.mockedName)

export const startNewPerimeter = createAsyncThunk<boolean, { kmlMode?: boolean }>(
    "startNewPerimeter",
    (payload) => {
        return !!payload.kmlMode
    }
)

export const selectMockedPerimeter = (s: RootState) => root(s).editing?.mockedPerimeter
